/**
 * useNostrLogin — NIP-07 and nsec login state management.
 * Fetches pubkey from extension or derives from nsec, then fetches kind-0 profile from relays.
 *
 * Auth state is stored in GLOBAL (unscoped) localStorage keys since it determines
 * which scope to use for everything else.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { SimplePool } from 'nostr-tools';
import {
  type Signer,
  Nip07Signer,
  NSecSigner,
  isNip07Available,
  storeSignerType,
  storeNsec,
  clearSignerStorage,
  createStoredSigner,
} from '@/lib/signer';

export interface NostrProfile {
  name?: string;
  display_name?: string;
  about?: string;
  picture?: string;
  nip05?: string;
  lud16?: string;
}

interface NostrLoginState {
  pubkey: string | null;
  profile: NostrProfile | null;
  signer: Signer | null;
  isLoading: boolean;
  isExtensionAvailable: boolean;
  /** Set relays for profile fetching (called by AppInner after scope is established). */
  setRelays: (relays: string[]) => void;
  /** Login via NIP-07 extension. */
  loginExtension: () => Promise<void>;
  /** Login via nsec string. */
  loginNsec: (nsec: string) => Promise<void>;
  logout: () => void;
  refreshProfile: () => Promise<void>;
}

export function useNostrLogin(): NostrLoginState {
  const [pubkey, setPubkey] = useState<string | null>(() => {
    return localStorage.getItem('tile-studio:pubkey');
  });
  const [profile, setProfile] = useState<NostrProfile | null>(() => {
    try {
      const cached = localStorage.getItem('tile-studio:profile');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [signer, setSigner] = useState<Signer | null>(() => createStoredSigner());
  const [isLoading, setIsLoading] = useState(false);

  // Relays ref — updated by AppInner after scope is established
  const relaysRef = useRef<string[]>([]);

  const setRelays = useCallback((relays: string[]) => {
    relaysRef.current = relays;
  }, []);

  const isExtensionAvailable = useMemo(() => isNip07Available(), []);

  const fetchProfile = useCallback(
    async (pk: string) => {
      const relays = relaysRef.current;
      if (relays.length === 0) return;
      const pool = new SimplePool();
      try {
        const events = await pool.querySync(relays, {
          kinds: [0],
          authors: [pk],
          limit: 1,
        });
        if (events.length > 0) {
          try {
            const parsed = JSON.parse(events[0].content) as NostrProfile;
            setProfile(parsed);
            localStorage.setItem('tile-studio:profile', JSON.stringify(parsed));
          } catch { /* malformed profile */ }
        }
      } finally {
        pool.destroy();
      }
    },
    [],
  );

  const loginExtension = useCallback(async () => {
    const s = new Nip07Signer();
    setIsLoading(true);
    try {
      const pk = await s.getPublicKey();
      localStorage.setItem('tile-studio:pubkey', pk);
      storeSignerType('nip07');
      setPubkey(pk);
      setSigner(s);
      await fetchProfile(pk);
    } finally {
      setIsLoading(false);
    }
  }, [fetchProfile]);

  const loginNsec = useCallback(async (nsec: string) => {
    setIsLoading(true);
    try {
      const s = NSecSigner.fromNsec(nsec);
      const pk = await s.getPublicKey();
      localStorage.setItem('tile-studio:pubkey', pk);
      storeSignerType('nsec');
      storeNsec(nsec);
      setPubkey(pk);
      setSigner(s);
      await fetchProfile(pk);
    } finally {
      setIsLoading(false);
    }
  }, [fetchProfile]);

  const logout = useCallback(() => {
    localStorage.removeItem('tile-studio:pubkey');
    localStorage.removeItem('tile-studio:profile');
    clearSignerStorage();
    setPubkey(null);
    setProfile(null);
    setSigner(null);
  }, []);

  const refreshProfile = useCallback(async () => {
    if (pubkey) await fetchProfile(pubkey);
  }, [pubkey, fetchProfile]);

  // Auto-fetch profile on mount if logged in and relays are available
  useEffect(() => {
    if (pubkey && !profile && relaysRef.current.length > 0) {
      fetchProfile(pubkey);
    }
  }, [pubkey, profile, fetchProfile]);

  return {
    pubkey,
    profile,
    signer,
    isLoading,
    isExtensionAvailable,
    setRelays,
    loginExtension,
    loginNsec,
    logout,
    refreshProfile,
  };
}
